import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Camera,
  Grid3X3,
  Download,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { useGalerie } from '../hooks/useGalerie';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -2rem;
  position: relative;
  z-index: 2;
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  text-align: center;
  color: var(--white);

  .number {
    font-size: 2.5rem;
    font-weight: 800;
    font-family: var(--font-secondary);
  }

  .label {
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const AlbumSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const AlbumButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.95rem;
  transition: var(--transition);
  background: ${props => props.$active ? 'var(--accent-gold)' : 'var(--white)'};
  color: ${props => props.$active ? 'var(--primary-blue-dark)' : 'var(--text-dark)'};
  border: 2px solid ${props => props.$active ? 'var(--accent-gold)' : 'var(--border-light)'};
  box-shadow: var(--shadow-sm);

  &:hover {
    border-color: var(--accent-gold);
    transform: translateY(-2px);
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: scale(1.02);
    box-shadow: var(--shadow-xl);

    .overlay {
      opacity: 1;
    }
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.3);
    font-size: 3rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const GalleryOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  opacity: 0;
  transition: var(--transition);

  .title {
    color: var(--white);
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .date {
    color: rgba(255,255,255,0.8);
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
`;

// Lightbox
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LightboxImage = styled.div`
  background: #222;
  border-radius: var(--radius-lg);
  min-width: 300px;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.2);
  font-size: 5rem;
  margin-bottom: 1rem;
`;

const LightboxInfo = styled.div`
  text-align: center;
  color: var(--white);

  h3 {
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: rgba(255,255,255,0.7);
    font-size: 0.95rem;
  }
`;

const LightboxNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$direction === 'left' ? 'left: -60px;' : 'right: -60px;'}
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  color: var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
  }

  @media (max-width: 768px) {
    ${props => props.$direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
    background: rgba(0,0,0,0.5);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  color: var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--secondary-red);
  }
`;

const DownloadButton = styled.button`
  position: absolute;
  top: -50px;
  right: 60px;
  padding: 0.75rem 1.25rem;
  border-radius: var(--radius-full);
  background: var(--accent-gold);
  color: var(--primary-blue-dark);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(254, 202, 0, 0.4);
  }
`;

const Galerie = () => {
  const { photos, albums, loading, error } = useGalerie();
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Construire la liste des albums
  const albumList = [
    { id: 'all', label: 'Toutes les photos' },
    ...albums.map(album => ({
      id: album,
      label: album.charAt(0).toUpperCase() + album.slice(1)
    }))
  ];

  const filteredPhotos = selectedAlbum === 'all'
    ? photos
    : photos.filter(img => img.album === selectedAlbum);

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const goToPrev = () => {
    setLightboxIndex(prev => (prev === 0 ? filteredPhotos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setLightboxIndex(prev => (prev === filteredPhotos.length - 1 ? 0 : prev + 1));
  };

  const currentImage = lightboxIndex !== null ? filteredPhotos[lightboxIndex] : null;

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Galerie Photos</HeroTitle>
          <HeroSubtitle>
            Découvrez les moments forts de la Médiature de la République
          </HeroSubtitle>
          
          <StatsBar>
            <StatItem>
              <div className="number">{loading ? '...' : photos.length}</div>
              <div className="label">Photos</div>
            </StatItem>
            <StatItem>
              <div className="number">{loading ? '...' : albums.length}</div>
              <div className="label">Albums</div>
            </StatItem>
          </StatsBar>
        </HeroContent>
      </Hero>

      <Container>
        <ContentWrapper>
          {/* Sélecteur d'albums */}
          <AlbumSelector>
            {albumList.map(album => (
              <AlbumButton 
                key={album.id}
                $active={selectedAlbum === album.id}
                onClick={() => setSelectedAlbum(album.id)}
              >
                <Grid3X3 size={18} />
                {album.label}
              </AlbumButton>
            ))}
          </AlbumSelector>

          {/* Grille de photos */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem' }}>
              <Loader2 size={48} className="animate-spin" />
              <p>Chargement...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#DC2626' }}>
              <p>Erreur de chargement</p>
              <p style={{ fontSize: '0.9rem' }}>{error}</p>
            </div>
          ) : filteredPhotos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-light)' }}>
              <ImageIcon size={64} />
              <p>Aucune photo dans cet album</p>
            </div>
          ) : (
            <GalleryGrid>
              {filteredPhotos.map((image, index) => (
                <GalleryItem 
                  key={image.id} 
                  onClick={() => openLightbox(index)}
                >
                  {image.imageUrl ? (
                    <img src={image.imageUrl} alt={image.title} loading="lazy" />
                  ) : (
                    <div className="placeholder">
                      <ImageIcon size={48} />
                    </div>
                  )}
                  <GalleryOverlay className="overlay">
                    <div className="title">{image.title}</div>
                    <div className="date">
                      <Calendar size={14} />
                      {image.date.toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </div>
                  </GalleryOverlay>
                </GalleryItem>
              ))}
            </GalleryGrid>
          )}
        </ContentWrapper>
      </Container>

      {/* Lightbox */}
      {currentImage && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeLightbox}>
              <X size={24} />
            </CloseButton>
            
            <DownloadButton>
              <Download size={18} />
              Télécharger
            </DownloadButton>

            <LightboxNav $direction="left" onClick={goToPrev}>
              <ChevronLeft size={24} />
            </LightboxNav>

            <LightboxImage>
              {currentImage.imageUrl ? (
                <img 
                  src={currentImage.imageUrl} 
                  alt={currentImage.title}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <ImageIcon size={80} />
              )}
            </LightboxImage>

            <LightboxNav $direction="right" onClick={goToNext}>
              <ChevronRight size={24} />
            </LightboxNav>

            <LightboxInfo>
              <h3>{currentImage.title}</h3>
              <p>
                <Calendar size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                {currentImage.date.toLocaleDateString('fr-FR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </LightboxInfo>
          </LightboxContent>
        </LightboxOverlay>
      )}
    </PageContainer>
  );
};

export default Galerie;
